/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, HandHeart, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import iconDonate from '/images/SJ-Icon-B_Donate2_Red_RGB.png';
import iconHeart from '/images/SJ-Icon-B_Heart_Red_RGB.png';
import iconChildren from '/images/SJ-Icon-B_Children_Red_RGB.png';


interface BrandPillar {
    id: 'mission' | 'freeToFamilies' | 'impact';
    icon: LucideIcon;
    img: string;
}

/** Inline SVG icons — no async image load, so no CLS risk from this section. */
const PILLARS: BrandPillar[] = [
    { id: 'freeToFamilies', icon: Heart, img: iconDonate },
    { id: 'mission', icon: HandHeart, img: iconHeart },
    { id: 'impact', icon: TrendingUp, img: iconChildren },
];

export default function BrandPillars(): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <div className="bg-footer-pillars-background py-12">
            <div className="section-container grid gap-8 md:grid-cols-3">
                {PILLARS.map(({ id, icon: Icon, img }) => (
                    <div key={id} className="flex flex-col items-center text-center pt-4 px-4">
                        {/** If an image is provided, display it */}
                        {img && <img src={img} className="w-[125px] h-[125px] mb-4" alt={t(`brandPillars.${id}.title`)}  />}
                        {!img && <Icon className="w-10 h-10 mb-4 text-header-accent-background" aria-hidden="true" />}
                        {/*<h3 className="text-lg font-semibold text-foreground mb-2">{t(`brandPillars.${id}.title`)}</h3>*/}
                        {/** The description contains HTML content */}
                        <p className="text-xl text-muted-foreground px-10" dangerouslySetInnerHTML={{ __html: t(`brandPillars.${id}.description`) }}></p>
                    </div>
                ))}
            </div>
        </div>
    );
}
